<script lang="ts" setup>
import { useFetch } from '@/home/hooks/fetch';
import { useAsyncData } from '@/home/hooks/async-data';

import markdownIt from 'markdown-it';
import SanitizeHtml from '@/common/components/sanitize-html/index.vue';

const { $fetch } = useFetch();

const { data } = useAsyncData(
  'home:template4:customize',
  () => $fetch('/customize/content/detail', { method: 'post' }),
);

const getRenderedContent = (type: number, text: string) => {
  if (type == 1) {
    const md = markdownIt({
      breaks: true,
      linkify: false,
      typographer: false,
      html: true,
      highlight: function(str, lang) {
        return `<pre class="language-${lang || 'javascript'}"><code>${str.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</code></pre>`;
      },
    });

    const defaultRenderLink = md.renderer.rules.link_open
      || ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));

    md.renderer.rules.link_open = function(tokens, idx, options, env, self) {
      tokens[idx].attrSet('target', '_blank');

      return defaultRenderLink(tokens, idx, options, env, self);
    };

    const defaultRenderImg = md.renderer.rules.image
      || ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));

    md.renderer.rules.image = function(tokens, idx, options, env, self) {
      tokens[idx].attrSet('class', 'picture');

      return defaultRenderImg(tokens, idx, options, env, self);
    };

    return md.render(text ?? '');
  }
  else {
    return text;
  }
}

const handleContentClick = (e: MouseEvent) => {
  const target = e.target as HTMLElement;

  if (target.tagName.toLowerCase() !== 'img') { return; }
  const parentLink = target.closest('a');

  if (!parentLink) {
    window.open((target as HTMLImageElement).src, '_blank');
    e.stopPropagation();  // 阻止冒泡
  }
}
</script>

<template>
  <div class="page-explore-detail">
    <SanitizeHtml
      class="detail-content line-numbers mt-40 min-h-500 overflow-x-auto break-anywhere text-16 color-#292929"
      :class="[data.contentType == 1 ? 'markdown' : 'rich-text']"
      :value="getRenderedContent(data.contentType, data.content)"
      :data-prismjs-copy="$t('common.prismjs.copy')"
      :data-prismjs-copy-error="$t('common.prismjs.copyError')"
      :data-prismjs-copy-success="$t('common.prismjs.copySuccess')"
      @click="handleContentClick"
      ref="contentEl"
      v-if="data"
    />
  </div>
</template>


<style lang="scss" scoped>
.page-explore-detail {
  .detail-content:deep() {
    h1 {
      font-size: 22px;
    }

    h2 {
      font-size: 20px;
    }

    h3 {
      margin-top: 1em;
      margin-bottom: 1em;
      font-size: 18px;
    }

    h1,
    h2 {
      margin-top: 2em;
      margin-bottom: 1em;
      font-weight: 500;
    }

    h4,
    h5,
    h6 {
      margin-top: 1em;
      margin-bottom: 1em;
      font-size: 16px;
    }

    ol,
    ul,
    menu {
      margin-top: 1em;
      margin-bottom: 1em;
      padding-left: 40px;
    }

    ol {
      list-style: decimal;
    }

    ul {
      list-style: disc;
    }

    p,
    section {
      margin-top: 1em;
      margin-bottom: 1em;
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
    }

    img {
      max-width: 100%;
      cursor: pointer;
    }

    table {
      td {
        border-color: gray;
        border-width: 1px;
      }
    }

    pre {
      overflow-x: auto;
    }

    a {
      color: #3d7cfa;
    }
  }

  .detail-content.markdown:deep() {
    table {
      display: block;
      width: 100%;
      margin-bottom: 16px;
      overflow-x: auto;

      th {
        background-color: #f7f7f7;
      }

      th,
      td {
        min-width: 100px;
        padding: 0.2em 0.4em;
        border: 1px solid #f7f7f7;
      }
    }

    hr {
      margin: 16px 0;
      border-top: 1px solid #f7f7f7;
    }

    blockquote {
      margin-bottom: 16px;
      padding: 10px 15px;
      color: #8b8b8b;
      background: #f7f7f7;
      border-left: 10px solid #d7d7d7;

      ol {
        margin: 0;
        padding-left: 24px;
      }
    }

    img {
      display: inline-block;
      margin: 0 auto;
    }

    :not(pre) > code {
      padding: 0.1em;
      color: #f85353;
      white-space: normal;
      background-color: #e5e5e5;
      border-radius: 0.3em;
    }

    .picture {
      display: block;
      margin: 0 auto;
    }
  }
}
</style>
